import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * <v2-set-add> wraps a native /cart/add form (works without JS). With JS it adds the
 * set variant via the AJAX cart and dispatches Horizon's CartLinesUpdateEvent with the
 * same detail shape as product-form.js, so the cart drawer and bubble update in place.
 */
class V2SetAdd extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[role="status"]');
    this.form?.addEventListener('submit', this.#onSubmit, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    // Horizon declares `const Theme` in a classic script: a global binding, not a window property.
    const routes = typeof Theme === 'undefined' ? null : Theme.routes;
    if (!this.form || !routes?.cart_add_url) return;
    event.preventDefault();
    if (this.button?.getAttribute('aria-disabled') === 'true') return;
    this.button?.setAttribute('aria-disabled', 'true');

    const formData = new FormData(this.form);
    const ids = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (ids.length) formData.append('sections', ids.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'v2-set-add',
          sourceId: this.dataset.productId || '',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: Boolean(response.status),
        },
      });
      if (this.status) this.status.textContent = response.status ? response.message || '' : this.dataset.addedText || '';
    } catch (error) {
      deferred.reject(error);
      this.form.submit();
    } finally {
      this.button?.removeAttribute('aria-disabled');
    }
  };
}

if (!customElements.get('v2-set-add')) customElements.define('v2-set-add', V2SetAdd);
